import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL, getAiConfig, updateAiConfig, getUserIssues, updateUserIssue, getAiTranscripts } from '../../api';

const SEVERITY_STYLES = {
    HIGH: 'bg-red-900/60 text-red-300 border-red-500/40',
    MEDIUM: 'bg-amber-900/60 text-amber-300 border-amber-500/40',
    LOW: 'bg-slate-800 text-gray-300 border-slate-600',
    REVIEW_REQUIRED: 'bg-purple-900/60 text-purple-300 border-purple-500/40',
};

const ISSUE_CATEGORY_STYLES = {
    bug: 'bg-red-900/60 text-red-300 border-red-500/40',
    feature_request: 'bg-blue-900/60 text-blue-300 border-blue-500/40',
    content_problem: 'bg-amber-900/60 text-amber-300 border-amber-500/40',
    feedback: 'bg-slate-800 text-slate-300 border-slate-600',
    other: 'bg-slate-800 text-slate-300 border-slate-600',
};

const ISSUE_STATUS_STYLES = {
    NEW: 'bg-amber-900/60 text-amber-300 border-amber-500/40',
    ACKNOWLEDGED: 'bg-blue-900/60 text-blue-300 border-blue-500/40',
    RESOLVED: 'bg-emerald-900/60 text-emerald-300 border-emerald-500/40',
};

const RUNNABLE_JOBS = [
    { jobName: 'EMPOWERMENT_QUEUE', label: 'Empowerment Queue', description: 'Flag requests pending more than 7 days' },
    { jobName: 'DEAD_LINK', label: 'Dead Links', description: 'HEAD-check every library and audio asset URL' },
];

const AdminDharmaMonitor = () => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const [tab, setTab] = useState('findings');
    const [findings, setFindings] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [statusFilter, setStatusFilter] = useState('OPEN');
    const [suggestionFilter, setSuggestionFilter] = useState('PENDING');
    const [loading, setLoading] = useState(false);
    const [runStatus, setRunStatus] = useState({});
    const [error, setError] = useState(null);
    const [promptValue, setPromptValue] = useState('');
    const [promptDefault, setPromptDefault] = useState('');
    const [promptHasOverride, setPromptHasOverride] = useState(false);
    const [promptSaving, setPromptSaving] = useState(false);
    const [promptNotice, setPromptNotice] = useState(null);
    const [issues, setIssues] = useState([]);
    const [issueFilter, setIssueFilter] = useState('NEW');
    const [issueSaving, setIssueSaving] = useState({});
    const [issueNotes, setIssueNotes] = useState({});
    // Transcript days are keyed in UTC (matches the backend day-index).
    const [transcripts, setTranscripts] = useState([]);
    const [transcriptDay, setTranscriptDay] = useState(() => new Date().toISOString().slice(0, 10));

    const authHeaders = useCallback(() => ({
        Authorization: `Bearer ${user?.idToken}`,
        'Content-Type': 'application/json',
    }), [user]);

    const loadFindings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = statusFilter ? `?status=${statusFilter}` : '';
            const res = await fetch(`${API_URL}/admin/cdm/findings${params}`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`Failed to load findings (${res.status})`);
            setFindings((await res.json()).findings || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, authHeaders]);

    const loadSuggestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = suggestionFilter ? `?status=${suggestionFilter}` : '';
            const res = await fetch(`${API_URL}/admin/suggestions${params}`, { headers: authHeaders() });
            if (!res.ok) throw new Error(`Failed to load suggestions (${res.status})`);
            setSuggestions((await res.json()).suggestions || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [suggestionFilter, authHeaders]);

    const loadAiConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        setPromptNotice(null);
        try {
            const data = await getAiConfig(user?.idToken);
            const override = typeof data.prompt === 'string' ? data.prompt : '';
            setPromptDefault(data.default || '');
            setPromptHasOverride(override.trim().length > 0);
            // Show the override if one exists, otherwise pre-fill with the
            // default so the admin edits from Dharmadhara's current behavior.
            setPromptValue(override.trim().length > 0 ? override : (data.default || ''));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    const loadUserIssues = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getUserIssues(user?.idToken, issueFilter === 'ALL' ? '' : issueFilter);
            setIssues(data.issues || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user, issueFilter]);

    const loadTranscripts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getAiTranscripts(user?.idToken, transcriptDay);
            setTranscripts(data.transcripts || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [user, transcriptDay]);

    useEffect(() => {
        if (!user?.idToken) return;
        if (tab === 'findings') loadFindings();
        else if (tab === 'suggestions') loadSuggestions();
        else if (tab === 'prompt') loadAiConfig();
        else if (tab === 'issues') loadUserIssues();
        else if (tab === 'transcripts') loadTranscripts();
    }, [user, tab, loadFindings, loadSuggestions, loadAiConfig, loadUserIssues, loadTranscripts]);

    const shiftTranscriptDay = (deltaDays) => {
        const d = new Date(`${transcriptDay}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + deltaDays);
        setTranscriptDay(d.toISOString().slice(0, 10));
    };

    const savePrompt = async () => {
        setPromptSaving(true);
        setError(null);
        setPromptNotice(null);
        try {
            await updateAiConfig(user?.idToken, promptValue);
            setPromptNotice('Saved. Dharmadhara is using this prompt now.');
            await loadAiConfig();
        } catch (err) {
            setError(err.message);
        } finally {
            setPromptSaving(false);
        }
    };

    const resetPrompt = async () => {
        if (!window.confirm('Reset to the built-in default prompt? This clears the current override for every visitor immediately.')) return;
        setPromptSaving(true);
        setError(null);
        setPromptNotice(null);
        try {
            await updateAiConfig(user?.idToken, '');
            setPromptNotice('Override cleared. Dharmadhara is back to the default prompt.');
            await loadAiConfig();
        } catch (err) {
            setError(err.message);
        } finally {
            setPromptSaving(false);
        }
    };

    const updateFinding = async (id, status) => {
        try {
            const res = await fetch(`${API_URL}/admin/cdm/findings/${encodeURIComponent(id)}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status }),
            });
            if (!res.ok) throw new Error(`Update failed (${res.status})`);
            loadFindings();
        } catch (err) {
            setError(err.message);
        }
    };

    const reviewSuggestion = async (id, action) => {
        try {
            const res = await fetch(`${API_URL}/admin/suggestions/${encodeURIComponent(id)}`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ action }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || `Review failed (${res.status})`);
            if (data.awaitingSecondApproval) {
                setError(null);
                alert('Approval recorded. A second custodian must also approve before this suggestion is accepted.');
            }
            loadSuggestions();
        } catch (err) {
            setError(err.message);
        }
    };

    const changeIssueStatus = async (id, status) => {
        setIssueSaving(s => ({ ...s, [id]: true }));
        setError(null);
        try {
            await updateUserIssue(user?.idToken, { id, status });
            await loadUserIssues();
        } catch (err) {
            setError(err.message);
        } finally {
            setIssueSaving(s => ({ ...s, [id]: false }));
        }
    };

    const saveIssueNote = async (id) => {
        setIssueSaving(s => ({ ...s, [id]: true }));
        setError(null);
        try {
            await updateUserIssue(user?.idToken, { id, adminNote: issueNotes[id] ?? '' });
            await loadUserIssues();
        } catch (err) {
            setError(err.message);
        } finally {
            setIssueSaving(s => ({ ...s, [id]: false }));
        }
    };

    const runJob = async (jobName) => {
        setRunStatus(s => ({ ...s, [jobName]: 'running' }));
        try {
            const res = await fetch(`${API_URL}/admin/cdm/run`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ jobName }),
            });
            if (!res.ok) throw new Error(`Run failed (${res.status})`);
            setRunStatus(s => ({ ...s, [jobName]: 'started' }));
            setTimeout(() => {
                setRunStatus(s => ({ ...s, [jobName]: null }));
                loadFindings();
            }, 15000);
        } catch (err) {
            setRunStatus(s => ({ ...s, [jobName]: 'error' }));
            setError(err.message);
        }
    };

    if (isLoading) return null;
    if (!isAuthenticated) return <Navigate to="/auth" replace />;
    if (!user?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-10 text-center max-w-sm">
                    <p className="text-white text-lg mb-4">Administrator access required.</p>
                    <Link to="/" className="text-amber-400 hover:underline text-sm">Return home</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-12 px-6">
            <div className="max-w-5xl mx-auto">
                <div className="mb-8">
                    <div className="inline-block bg-slate-900/70 backdrop-blur-sm rounded-xl px-8 py-6">
                        <h1 className="text-3xl font-bold text-white mb-1">Dharma Monitor</h1>
                        <p className="text-gray-300 text-sm">
                            Continuous Dharma Monitor findings and Dharmadhara content suggestions.
                            The AI flags and proposes; humans decide.
                        </p>
                    </div>
                </div>

                {/* Run-now controls */}
                <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5 mb-6">
                    <h2 className="text-white font-semibold mb-3">Run a scan now</h2>
                    <div className="flex flex-wrap gap-3">
                        {RUNNABLE_JOBS.map(({ jobName, label, description }) => (
                            <button
                                key={jobName}
                                onClick={() => runJob(jobName)}
                                disabled={runStatus[jobName] === 'running' || runStatus[jobName] === 'started'}
                                className="bg-slate-800 border border-slate-600 hover:border-amber-500 disabled:opacity-60 rounded-lg px-4 py-3 text-left transition-all"
                                title={description}
                            >
                                <div className="text-white text-sm font-medium">{label}</div>
                                <div className="text-gray-500 text-xs mt-0.5">
                                    {runStatus[jobName] === 'started' ? 'Started — findings will refresh shortly'
                                        : runStatus[jobName] === 'running' ? 'Starting…'
                                        : runStatus[jobName] === 'error' ? 'Failed to start — try again'
                                        : description}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-4">
                    {[['findings', 'CDM Findings'], ['suggestions', 'Content Suggestions'], ['issues', 'User Issues'], ['transcripts', 'Transcripts'], ['prompt', 'Prompt']].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setTab(key)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                tab === key
                                    ? 'bg-amber-600 text-white'
                                    : 'bg-slate-800/80 text-gray-400 hover:text-gray-200'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                {error && (
                    <div className="bg-red-900/40 border border-red-500/40 text-red-300 text-sm rounded-lg px-4 py-3 mb-4">
                        {error}
                    </div>
                )}

                {tab === 'findings' && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-white font-semibold">Findings</h2>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-slate-800 border border-slate-600 text-gray-300 text-sm rounded-lg px-3 py-1.5"
                            >
                                <option value="OPEN">Open</option>
                                <option value="ACKNOWLEDGED">Acknowledged</option>
                                <option value="RESOLVED">Resolved</option>
                                <option value="DISMISSED">Dismissed</option>
                                <option value="">All</option>
                            </select>
                        </div>
                        {loading ? (
                            <p className="text-gray-500 text-sm">Loading…</p>
                        ) : findings.length === 0 ? (
                            <p className="text-gray-500 text-sm">No findings. The archive rests undisturbed.</p>
                        ) : (
                            <div className="space-y-3">
                                {findings.map(f => (
                                    <div key={f.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <span className={`text-xs border rounded-full px-2 py-0.5 ${SEVERITY_STYLES[f.severity] || SEVERITY_STYLES.LOW}`}>
                                                {f.severity}
                                            </span>
                                            <span className="text-gray-500 text-xs">{f.jobName}</span>
                                            <span className="text-gray-600 text-xs">{new Date(f.createdAt).toLocaleString()}</span>
                                        </div>
                                        <p className="text-gray-200 text-sm mb-1">{f.finding}</p>
                                        {f.claudeReasoning && (
                                            <p className="text-gray-400 text-xs mb-1"><span className="text-gray-500">Reasoning:</span> {f.claudeReasoning}</p>
                                        )}
                                        {f.suggestedAction && (
                                            <p className="text-gray-400 text-xs mb-2"><span className="text-gray-500">Suggested:</span> {f.suggestedAction}</p>
                                        )}
                                        {f.status === 'OPEN' ? (
                                            <div className="flex gap-2 mt-2">
                                                <button onClick={() => updateFinding(f.id, 'ACKNOWLEDGED')} className="text-xs bg-slate-700 hover:bg-slate-600 text-gray-200 rounded px-3 py-1">Acknowledge</button>
                                                <button onClick={() => updateFinding(f.id, 'RESOLVED')} className="text-xs bg-emerald-800 hover:bg-emerald-700 text-emerald-100 rounded px-3 py-1">Resolve</button>
                                                <button onClick={() => updateFinding(f.id, 'DISMISSED')} className="text-xs bg-slate-700 hover:bg-slate-600 text-gray-400 rounded px-3 py-1">Dismiss</button>
                                            </div>
                                        ) : (
                                            <p className="text-gray-600 text-xs mt-1">
                                                {f.status.toLowerCase()} by {f.resolvedBy}{f.resolvedAt ? ` · ${new Date(f.resolvedAt).toLocaleString()}` : ''}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'suggestions' && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-white font-semibold">Dharmadhara Suggestions</h2>
                            <select
                                value={suggestionFilter}
                                onChange={(e) => setSuggestionFilter(e.target.value)}
                                className="bg-slate-800 border border-slate-600 text-gray-300 text-sm rounded-lg px-3 py-1.5"
                            >
                                <option value="PENDING">Pending</option>
                                <option value="APPROVED">Approved</option>
                                <option value="REJECTED">Rejected</option>
                                <option value="">All</option>
                            </select>
                        </div>
                        {loading ? (
                            <p className="text-gray-500 text-sm">Loading…</p>
                        ) : suggestions.length === 0 ? (
                            <p className="text-gray-500 text-sm">No suggestions in this state.</p>
                        ) : (
                            <div className="space-y-3">
                                {suggestions.map(s => (
                                    <div key={s.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <span className="text-xs bg-slate-700 text-gray-300 rounded-full px-2 py-0.5">{s.type}</span>
                                            <span className="text-xs bg-slate-700 text-gray-300 rounded-full px-2 py-0.5">{s.contentType}</span>
                                            {Number(s.requiredApprovals) > 1 && (
                                                <span className="text-xs bg-purple-900/60 border border-purple-500/40 text-purple-300 rounded-full px-2 py-0.5">
                                                    Two approvals required · {(s.approvals || []).length}/{s.requiredApprovals}
                                                </span>
                                            )}
                                            <span className="text-gray-600 text-xs">{new Date(s.createdAt).toLocaleString()}</span>
                                            <span className="text-gray-600 text-xs">from {s.submittedBy === 'anonymous' ? 'an anonymous visitor' : s.submittedBy}</span>
                                        </div>
                                        {s.userNote && (
                                            <p className="text-gray-300 text-sm mb-2">“{s.userNote}”</p>
                                        )}
                                        <pre className="bg-slate-950/70 border border-slate-700 rounded p-3 text-xs text-gray-300 overflow-x-auto mb-2 whitespace-pre-wrap">
                                            {JSON.stringify(s.suggestedContent, null, 2)}
                                        </pre>
                                        {s.contentId && (
                                            <p className="text-gray-500 text-xs mb-2">Corrects existing content: {s.contentId}</p>
                                        )}
                                        {s.status === 'PENDING' ? (
                                            <div className="flex gap-2">
                                                <button onClick={() => reviewSuggestion(s.id, 'APPROVE')} className="text-xs bg-emerald-800 hover:bg-emerald-700 text-emerald-100 rounded px-3 py-1">Approve</button>
                                                <button onClick={() => reviewSuggestion(s.id, 'REJECT')} className="text-xs bg-red-900/70 hover:bg-red-800 text-red-200 rounded px-3 py-1">Reject</button>
                                            </div>
                                        ) : (
                                            <p className="text-gray-600 text-xs">
                                                {s.status.toLowerCase()} by {s.reviewedBy}{s.reviewedAt ? ` · ${new Date(s.reviewedAt).toLocaleString()}` : ''}
                                            </p>
                                        )}
                                        {s.status === 'APPROVED' && (
                                            <p className="text-amber-400/80 text-xs mt-1">
                                                Approved — apply the change through the content admin tools. Nothing publishes automatically.
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'issues' && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-white font-semibold">User-Reported Issues</h2>
                            <select
                                value={issueFilter}
                                onChange={(e) => setIssueFilter(e.target.value)}
                                className="bg-slate-800 border border-slate-600 text-gray-300 text-sm rounded-lg px-3 py-1.5"
                            >
                                <option value="NEW">New</option>
                                <option value="ACKNOWLEDGED">Acknowledged</option>
                                <option value="RESOLVED">Resolved</option>
                                <option value="ALL">All</option>
                            </select>
                        </div>
                        {loading ? (
                            <p className="text-gray-500 text-sm">Loading…</p>
                        ) : issues.length === 0 ? (
                            <p className="text-gray-500 text-sm">No issues.</p>
                        ) : (
                            <div className="space-y-3">
                                {issues.map(i => (
                                    <div key={i.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center gap-2 mb-2">
                                            <span className={`text-xs border rounded-full px-2 py-0.5 ${ISSUE_CATEGORY_STYLES[i.category] || ISSUE_CATEGORY_STYLES.other}`}>
                                                {(i.category || 'other').replace(/_/g, ' ')}
                                            </span>
                                            <span className={`text-xs border rounded-full px-2 py-0.5 ${ISSUE_STATUS_STYLES[i.status] || ISSUE_STATUS_STYLES.NEW}`}>
                                                {i.status}
                                            </span>
                                            {i.possibleDuplicateOf && (
                                                <span className="text-xs text-gray-500 italic" title={`Possible duplicate of ${i.possibleDuplicateOf}`}>
                                                    possible duplicate
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-gray-100 text-sm font-bold mb-1">{i.summary}</p>
                                        {i.details && (
                                            <p className="text-gray-300 text-sm mb-2 whitespace-pre-wrap">{i.details}</p>
                                        )}
                                        <p className="text-gray-600 text-xs mb-3">
                                            {i.area ? `${i.area} · ` : ''}
                                            {i.submittedBy === 'anonymous' ? 'an anonymous visitor' : i.submittedBy}
                                            {i.submittedAt ? ` · ${new Date(i.submittedAt).toLocaleString()}` : ''}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {i.status === 'NEW' && (
                                                <button onClick={() => changeIssueStatus(i.id, 'ACKNOWLEDGED')} disabled={issueSaving[i.id]} className="text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-gray-200 rounded px-3 py-1">Acknowledge</button>
                                            )}
                                            {(i.status === 'NEW' || i.status === 'ACKNOWLEDGED') && (
                                                <button onClick={() => changeIssueStatus(i.id, 'RESOLVED')} disabled={issueSaving[i.id]} className="text-xs bg-emerald-800 hover:bg-emerald-700 disabled:opacity-60 text-emerald-100 rounded px-3 py-1">Resolve</button>
                                            )}
                                            {i.status === 'RESOLVED' && (
                                                <button onClick={() => changeIssueStatus(i.id, 'NEW')} disabled={issueSaving[i.id]} className="text-xs bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-gray-200 rounded px-3 py-1">Reopen</button>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <textarea
                                                value={issueNotes[i.id] ?? i.adminNote ?? ''}
                                                onChange={(e) => setIssueNotes(n => ({ ...n, [i.id]: e.target.value }))}
                                                rows={2}
                                                placeholder="Admin note (optional)…"
                                                className="w-full bg-slate-950/70 border border-slate-700 focus:border-amber-500 text-gray-200 text-xs rounded-lg p-2 resize-y outline-none"
                                            />
                                            <div className="flex items-center justify-between">
                                                {i.updatedBy && (
                                                    <span className="text-gray-600 text-xs">
                                                        last updated by {i.updatedBy}{i.updatedAt ? ` · ${new Date(i.updatedAt).toLocaleString()}` : ''}
                                                    </span>
                                                )}
                                                <button
                                                    onClick={() => saveIssueNote(i.id)}
                                                    disabled={issueSaving[i.id]}
                                                    className="text-xs bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white rounded px-3 py-1 ml-auto"
                                                >
                                                    {issueSaving[i.id] ? 'Saving…' : 'Save note'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'transcripts' && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                            <h2 className="text-white font-semibold">Dharmadhara Transcripts</h2>
                            <div className="flex items-center gap-2">
                                <button onClick={() => shiftTranscriptDay(-1)} className="text-xs bg-slate-800 border border-slate-600 hover:border-amber-500 text-gray-300 rounded px-2 py-1.5">← Prev day</button>
                                <input
                                    type="date"
                                    value={transcriptDay}
                                    onChange={(e) => e.target.value && setTranscriptDay(e.target.value)}
                                    className="bg-slate-800 border border-slate-600 text-gray-300 text-sm rounded-lg px-3 py-1"
                                />
                                <button onClick={() => shiftTranscriptDay(1)} className="text-xs bg-slate-800 border border-slate-600 hover:border-amber-500 text-gray-300 rounded px-2 py-1.5">Next day →</button>
                                <button onClick={loadTranscripts} className="text-xs bg-slate-700 hover:bg-slate-600 text-gray-200 rounded px-3 py-1.5">Refresh</button>
                            </div>
                        </div>
                        <p className="text-gray-500 text-xs mb-4">
                            De-identified audit log for tuning and debugging — raw message in, reply out.
                            Identities are stored only as a hash (same hash = same visitor), never as an
                            email or IP. Days are UTC. Entries expire automatically after 90 days.
                        </p>
                        {loading ? (
                            <p className="text-gray-500 text-sm">Loading…</p>
                        ) : transcripts.length === 0 ? (
                            <p className="text-gray-500 text-sm">No conversations on this day.</p>
                        ) : (
                            <div className="space-y-3">
                                {transcripts.map(t => (
                                    <div key={t.id} className="bg-slate-800/60 border border-slate-700 rounded-lg p-4">
                                        <div className="flex flex-wrap items-center gap-2 mb-3">
                                            <span className="text-gray-500 text-xs">{new Date(t.createdAt).toLocaleTimeString()}</span>
                                            <span className="text-xs bg-slate-700 text-gray-300 rounded-full px-2 py-0.5 font-mono" title="Hashed visitor identity — same hash means same visitor">
                                                {(t.userHash || '').slice(0, 8)}
                                            </span>
                                            <span className={`text-xs border rounded-full px-2 py-0.5 ${t.authenticated ? 'bg-blue-900/60 text-blue-300 border-blue-500/40' : 'bg-slate-800 text-gray-400 border-slate-600'}`}>
                                                {t.authenticated ? 'signed in' : 'anonymous'}
                                            </span>
                                            {t.gated && (
                                                <span className="text-xs bg-purple-900/60 border border-purple-500/40 text-purple-300 rounded-full px-2 py-0.5" title="The scope gate answered with the standard off-topic reply">
                                                    off-topic gate
                                                </span>
                                            )}
                                            {(t.toolsUsed || []).map((name, idx) => (
                                                <span key={idx} className="text-xs bg-amber-900/40 border border-amber-500/30 text-amber-300 rounded-full px-2 py-0.5">
                                                    {name}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="mb-2">
                                            <p className="text-gray-500 text-xs mb-0.5">Visitor</p>
                                            <p className="text-gray-200 text-sm whitespace-pre-wrap">{t.userMessage}</p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 text-xs mb-0.5">Dharmadhara</p>
                                            <p className="text-gray-300 text-sm whitespace-pre-wrap">{t.assistantReply}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {tab === 'prompt' && (
                    <div className="bg-slate-900/70 backdrop-blur-sm rounded-xl p-5">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-white font-semibold">Dharmadhara System Prompt</h2>
                            <span className="text-gray-500 text-xs">
                                {promptHasOverride ? 'Custom override active' : 'Using built-in default'}
                            </span>
                        </div>
                        <div className="bg-amber-900/40 border border-amber-500/40 text-amber-200 text-xs rounded-lg px-4 py-3 mb-4">
                            Caution: editing this changes Dharmadhara's behavior for every visitor immediately.
                            Leave it blank and save to reset to the built-in default.
                        </div>
                        {promptNotice && (
                            <div className="bg-emerald-900/40 border border-emerald-500/40 text-emerald-200 text-sm rounded-lg px-4 py-3 mb-4">
                                {promptNotice}
                            </div>
                        )}
                        {loading ? (
                            <p className="text-gray-500 text-sm">Loading…</p>
                        ) : (
                            <>
                                <textarea
                                    value={promptValue}
                                    onChange={(e) => setPromptValue(e.target.value)}
                                    spellCheck={false}
                                    rows={22}
                                    maxLength={20000}
                                    className="w-full bg-slate-950/80 border border-slate-700 focus:border-amber-500 text-gray-200 text-xs font-mono rounded-lg p-4 resize-y outline-none"
                                    placeholder="Enter Dharmadhara's system prompt…"
                                />
                                <div className="flex items-center justify-between mt-2">
                                    <span className={`text-xs ${promptValue.length > 20000 ? 'text-red-400' : 'text-gray-500'}`}>
                                        {promptValue.length.toLocaleString()} / 20,000 characters
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={resetPrompt}
                                            disabled={promptSaving}
                                            className="text-sm bg-slate-700 hover:bg-slate-600 disabled:opacity-60 text-gray-200 rounded-lg px-4 py-2"
                                        >
                                            Reset to default
                                        </button>
                                        <button
                                            onClick={savePrompt}
                                            disabled={promptSaving || promptValue.length > 20000}
                                            className="text-sm bg-amber-600 hover:bg-amber-500 disabled:opacity-60 text-white rounded-lg px-4 py-2"
                                        >
                                            {promptSaving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                </div>
                                {promptDefault && promptValue.trim() !== promptDefault.trim() && (
                                    <p className="text-gray-600 text-xs mt-3">
                                        This differs from the built-in default. "Reset to default" clears the stored override.
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDharmaMonitor;
